'use client';

import { ComponentProps, FC, useCallback, useMemo } from 'react';
import { usePathname } from '@/lib/router';
import { Navigation } from '@pcmp/react-coworker-components';
import CheckmarkIcon from '@ingka/ssr-icon/paths/checkmark';
import DocumentIcon from '@ingka/ssr-icon/paths/document-magnifying-glass';
import AllenKeyIcon from '@ingka/ssr-icon/paths/allen-key';
import PlusIcon from '@ingka/ssr-icon/paths/plus';
import DocumentPencilIcon from '@ingka/ssr-icon/paths/document-pencil';
import CoWorkerIcon from '@ingka/ssr-icon/paths/co-worker';
import GearIcon from '@ingka/ssr-icon/paths/gear';
import BoxIcon from '@ingka/ssr-icon/paths/box';
import WardrobeIcon from '@ingka/ssr-icon/paths/wardrobe';
import MailArrowOut from '@ingka/ssr-icon/paths/mail-arrow-out';
import MetricsIcon from '@ingka/ssr-icon/paths/arrow-trend-up';
import { useModal, useToast } from '@pcmp/react-providers';
import {
  getAdminActivityLogPath,
  getAdminCompletedTasksPath,
  getAdminItemsGlobalPath,
  getAdminItemsPath,
  getAdminPendingTasksPath,
  getAdminUsersAdminsPath,
  getAdminUsersPath,
  getAdminUsersExternalPath,
  getAdminLocationDetailPath,
  getAdminWarehouseDetailPath,
  getAdminInvitationDetailPath,
} from '@/lib/utils/router';
import { useActiveLocation } from '@/redux/features/shopping-bag/hooks';
import { useTranslation } from 'react-i18next';
import {
  OrderStatus,
  OrderScope,
  useCreateWarehouseMutation,
  WarehouseArgs,
  useUpdateWarehouseMutation,
} from '@/redux/back-dashboard-api';
import { useSearchParams } from '@/lib/router';
import {
  useLocationUsers,
  useOrdersByScope,
} from '@/redux/back-dashboard-api/hooks';
import ClockIcon from '@ingka/ssr-icon/paths/clock';
import {
  isOrderActive,
  isOrderInPreparation,
  isOrderInReturn,
} from '@/lib/utils/orders';
import Skeleton from '@ingka/skeleton';
import { useMenuContext } from '@/components/layouts/page-layout/menu';
import { CreateStoragePointFormData } from '@/lib/schemas/create-storage-point';
import { ErrorToast } from '@/components/helpers/toast-messages';
import { env } from '@/lib/env';
import { Location } from '@/redux/back-dashboard-api';

const { SideItemContainer: _SideItemContainer } = Navigation;

const SideItemContainer: FC<ComponentProps<typeof _SideItemContainer>> = (
  props
) => <_SideItemContainer className="px-4" {...props} />;

const serializeWarehouse = (
  warehouse: CreateStoragePointFormData,
  locationId: string
): { locationId: string; warehouse: WarehouseArgs } => {
  return {
    locationId,
    warehouse: {
      name: warehouse.name,
    },
  };
};

const getWarehouseCount = (
  stock: Location['stock'] = [],
  warehouseId: string
): number =>
  stock.reduce(
    (acc, stockItem) =>
      acc +
      (stockItem.availability.find(
        (availability) => availability._warehouseId === warehouseId
      )?.amount || 0),
    0
  );

const openMetricsDashboard = () => {
  window.open(env.METRICS_DASHBOARD_URL, '_blank', 'noopener,noreferrer');
};

export const AdminSideMenu: FC = () => {
  // Hooks
  const { t } = useTranslation();
  const pathname = usePathname() || '';
  const searchParams = useSearchParams();
  const { openModal } = useModal();
  const { pushToast } = useToast();
  const { goTo } = useMenuContext();
  // Data
  const { data: location } = useActiveLocation();
  const { data: ordersResponse } = useOrdersByScope(
    OrderScope.Location,
    location?._id || '',
    {
      skip: !location,
    }
  );
  // Event handlers
  const [createWareHouse] = useCreateWarehouseMutation();
  const [updateWareHouse] = useUpdateWarehouseMutation();
  const warehouses = useMemo(
    () =>
      (location?.warehouses || []).map((warehouse) => ({
        ...warehouse,
        label: location ? getWarehouseCount(location.stock, warehouse._id) : 0,
      })),
    [location]
  );
  const { data: usersResponse } = useLocationUsers(location?._id || '', {
    skip: !location,
  });
  const counter = useMemo(
    () => ({
      completed:
        ordersResponse?.filter(({ status }) => status === OrderStatus.Completed)
          .length || 0,
      pending:
        ordersResponse?.filter(({ status }) => status === OrderStatus.Pending)
          .length || 0,
      allUsers: usersResponse?.users?.length || 0,
      adminUsers: usersResponse?.administrators?.length || 0,
      externalUsers: usersResponse?.externalUsers?.length || 0,
      active: ordersResponse?.filter(isOrderActive).length || 0,
      preparation: ordersResponse?.filter(isOrderInPreparation).length || 0,
      return: ordersResponse?.filter(isOrderInReturn).length || 0,
    }),
    [ordersResponse, usersResponse]
  );

  const handleCreateStoragePoint = useCallback(
    async (data: CreateStoragePointFormData) => {
      // Prepare data
      if (!data?.locationId) return false;
      const { locationId } = data;
      const warehouse = serializeWarehouse(data, locationId);

      // Send request
      const response = await createWareHouse(warehouse);
      const isError =
        (response && 'error' in response) ||
        ('data' in response && !response.data.createWarehouse.result);

      // Show toast
      const toastContent = isError ? (
        <ErrorToast error={response.error} />
      ) : (
        t('page.admin.items.newStoragePoint.toastSmsOk')
      );
      pushToast({ message: toastContent });

      return !isError;
    },
    [createWareHouse, pushToast, t]
  );

  const handleUpdateStoragePoint = useCallback(
    async (data: CreateStoragePointFormData & { warehouseId?: string }) => {
      // Prepare data
      if (!data?.locationId || !data?.warehouseId) return false;
      const { locationId, warehouseId, name } = data;

      // Send request
      const response = await updateWareHouse({
        locationId,
        warehouseId,
        warehouse: { name },
      });
      const isError =
        (response && 'error' in response) ||
        ('data' in response && !response.data.updateWarehouse.result);

      // Show toast
      const toastContent = isError ? (
        <ErrorToast error={response.error} />
      ) : (
        t('page.admin.items.editStoragePoint.toastSmsOk')
      );
      pushToast({ message: toastContent });

      return !isError;
    },
    [updateWareHouse, pushToast, t]
  );

  const handleEditStoragePointSettings = useCallback(
    (locationId: string, warehouseId: string, currentName: string) =>
      async () => {
        const component = await import(
          '../../../siders/new-storage-point'
        ).then((m) => m.NewStoragePointSider);

        openModal({
          component,
          props: {
            onCreate: handleUpdateStoragePoint,
            data: { name: currentName, locationId, warehouseId },
          },
        });
      },
    [handleUpdateStoragePoint, openModal]
  );

  // Event handlers
  const handleNewStoragePointSettings = useCallback(
    (locationId: string) => async () => {
      const component = await import('../../../siders/new-storage-point').then(
        (m) => m.NewStoragePointSider
      );

      openModal({
        component,
        props: {
          onCreate: handleCreateStoragePoint,
          data: { name: '', locationId: locationId },
        },
      });
    },
    [handleCreateStoragePoint, openModal]
  );

  if (!location)
    return Array.from({ length: 6 })
      .fill(0)
      .map((_, index) => (
        <Skeleton key={index} height="40px" className="my-2" />
      ));

  return (
    <>
      <SideItemContainer
        icon={ClockIcon}
        title={t('routes.adminPendingTasks')}
        label={counter.pending}
        expanded={pathname.includes('orders') || pathname === '/admin'}
      >
        <SideItemContainer
          title={t('routes.adminActiveOrders')}
          label={counter.active}
          active={
            searchParams?.get('status') === OrderStatus.Pending &&
            searchParams?.get('cat') === 'active'
          }
          onClick={goTo(getAdminPendingTasksPath('active'))}
        />

        <SideItemContainer
          title={t('routes.adminPreparationOrders')}
          label={counter.preparation}
          active={
            searchParams?.get('status') === OrderStatus.Pending &&
            searchParams?.get('cat') === 'preparation'
          }
          onClick={goTo(getAdminPendingTasksPath('preparation'))}
        />

        <SideItemContainer
          title={t('routes.adminExpectedReturns')}
          label={counter.return}
          active={
            searchParams?.get('status') === OrderStatus.Pending &&
            searchParams?.get('cat') === 'return'
          }
          onClick={goTo(getAdminPendingTasksPath('return'))}
        />
      </SideItemContainer>

      <SideItemContainer
        icon={CheckmarkIcon}
        title={t('routes.adminCompletedTasks')}
        label={counter.completed}
        active={searchParams?.get('status') === OrderStatus.Completed}
        onClick={goTo(getAdminCompletedTasksPath())}
      />

      <SideItemContainer
        icon={DocumentIcon}
        title={t('routes.adminActivities')}
        active={pathname === getAdminActivityLogPath()}
        onClick={goTo(getAdminActivityLogPath())}
      />

      <SideItemContainer
        icon={AllenKeyIcon}
        title={t('routes.adminItems')}
        expanded={pathname.includes('/admin/items')}
      >
        <SideItemContainer
          title={t('routes.adminItemsGlobal')}
          active={pathname === getAdminItemsGlobalPath()}
          onClick={goTo(getAdminItemsGlobalPath())}
        />

        <SideItemContainer
          title={t('routes.adminItemsLocal')}
          active={pathname === getAdminItemsPath()}
          onClick={goTo(getAdminItemsPath())}
        />
      </SideItemContainer>

      <SideItemContainer
        icon={WardrobeIcon}
        title={t('routes.adminItemsStorages')}
        label={location?.warehouses.length}
        expanded={warehouses.some(
          (warehouse) => pathname === getAdminWarehouseDetailPath(warehouse._id)
        )}
      >
        <>
          {warehouses.map((warehouse) => (
            <SideItemContainer
              key={warehouse._id}
              icon={BoxIcon}
              title={warehouse.name}
              label={warehouse.label}
              active={pathname === getAdminWarehouseDetailPath(warehouse._id)}
              onClick={goTo(getAdminWarehouseDetailPath(warehouse._id))}
              button={{
                icon: DocumentPencilIcon,
                onClick: handleEditStoragePointSettings(
                  location._id,
                  warehouse._id,
                  warehouse.name
                ),
              }}
            />
          ))}

          {location && (
            <SideItemContainer
              icon={PlusIcon}
              title={t('label.newStorage')}
              onClick={handleNewStoragePointSettings(location?._id)}
            />
          )}
        </>
      </SideItemContainer>

      <SideItemContainer
        icon={CoWorkerIcon}
        title={t('routes.adminUsers')}
        expanded={pathname.includes('/admin/users')}
      >
        <SideItemContainer
          title={t('routes.adminUsersAll')}
          label={counter.allUsers}
          onClick={goTo(getAdminUsersPath())}
          active={searchParams?.get('type') === 'users'}
        />

        <SideItemContainer
          title={t('routes.adminUsersAdmins')}
          label={counter.adminUsers}
          onClick={goTo(getAdminUsersAdminsPath())}
          active={searchParams?.get('type') === 'administrators'}
        />

        <SideItemContainer
          title={t('routes.adminUsersExternal')}
          label={counter.externalUsers}
          onClick={goTo(getAdminUsersExternalPath())}
          active={searchParams?.get('type') === 'externalUsers'}
        />

        {/*   <SideItemContainer title={t('routes.adminUsersAll')} label={0} /> */}
        {/**/}
        {/*   <SideItemContainer title={t('routes.adminUsersAdmins')} label={0}> */}
        {/*     <SideItemContainer */}
        {/*       title={t('routes.adminUsersApprovers')} */}
        {/*       label={0} */}
        {/*     /> */}
        {/**/}
        {/*     <SideItemContainer */}
        {/*       icon={PeopleIcon} */}
        {/*       title="User Group 1" */}
        {/*       label={0} */}
        {/*     /> */}
        {/**/}
        {/*     <SideItemContainer */}
        {/*       icon={PeopleIcon} */}
        {/*       title="User Group 2" */}
        {/*       label={0} */}
        {/*     /> */}
        {/**/}
        {/*     <SideItemContainer icon={PlusIcon} title="New user group" /> */}
        {/*   </SideItemContainer> */}
        {/* </SideItemContainer> */}
      </SideItemContainer>

      <SideItemContainer
        icon={MailArrowOut}
        title={t('routes.adminInvitations')}
        onClick={goTo(getAdminInvitationDetailPath())}
        active={pathname === getAdminInvitationDetailPath()}
      />

      <SideItemContainer
        icon={MetricsIcon}
        title={t('page.admin.metricsDashboardLink')}
        onClick={openMetricsDashboard}
      />

      <SideItemContainer
        icon={GearIcon}
        title={t('routes.adminSettings')}
        onClick={goTo(getAdminLocationDetailPath())}
        active={pathname === getAdminLocationDetailPath()}
      />
    </>
  );
};
